import { http } from '@/lib/http'
import { deviceId } from '@/lib/device-id'
import type { TokenPair } from '@/types/api.types'
import type { ProfileType, UserSession } from '@/types/models'

/** How a sign-in code reached the person (LP-1033). */
export type OtpChannel = 'sms' | 'whatsapp' | 'email'

/** A channel the person may ask for instead; `hint` shows where it goes without giving it away ("a•••@gmail.com"). */
export interface OtpAlternative {
  channel: OtpChannel
  hint?: string
}

/** What send and resend return. No `channel` (an older backend, the flag off, the demo account) means SMS. */
export interface OtpDelivery {
  token: string
  channel?: OtpChannel
  alternatives?: OtpAlternative[]
}

export interface CompleteProfileBody {
  firstName: string
  lastName: string
  email: string
  defaultProfile: ProfileType
  /** S3 key from the media upload; omitted when no photo was added. */
  avatar?: string
}

export const authApi = {
  /** Who the stored token belongs to. 401 means the session is gone. */
  me: (signal?: AbortSignal) => http.get<UserSession>('/auth/me', { signal }),

  refresh: (refreshToken: string) => http.post<TokenPair>('/auth/token/refresh', { refreshToken }, { skipAuth: true }),

  /** Revokes the pair; the refresh token is optional for callers that no longer have it. */
  // `deviceId` also removes this browser's push token (LP-943); the X-Device-Id header carries the same id.
  logout: (refreshToken?: string | null) => http.post<null>('/auth/logout', refreshToken ? { refreshToken, deviceId: deviceId() } : { deviceId: deviceId() }),

  /** `mobile` is "<cc> <number>", e.g. "91 9876543210". Returns the OTP token for verify/resend. */
  // `countryCode` is the ISO2 the user picked, optional on purpose: +1 is the US and Canada, so the server cannot always infer it.
  // The server treats it as a hint and falls back to the dial code; older clients omit it.
  sendOtp: (mobile: string, countryCode?: string) =>
    http.post<OtpDelivery>('/auth/otp/send', countryCode ? { mobile, countryCode } : { mobile }, { skipAuth: true }),

  /** `channel` asks for one of the alternatives the last send offered; without it the code goes the default way again. */
  resendOtp: (token: string, channel?: OtpChannel) =>
    http.post<OtpDelivery>('/auth/otp/resend', channel ? { token, channel } : { token }, { skipAuth: true }),

  /** `ref` is a refer & earn invite token (LP-998); the backend credits it only when this sign-in creates the account. */
  verifyOtp: (token: string, otp: string, ref?: string) =>
    http.post<TokenPair & { user: UserSession }>('/auth/otp/verify', ref ? { token, otp, ref } : { token, otp }, { skipAuth: true }),

  /** First-login onboarding. Returns a new access token (the refresh token stays) and the user. */
  completeProfile: (body: CompleteProfileBody) => http.post<{ accessToken: string; user: UserSession }>('/profile/complete', body),

  /** Marks the email verified from the welcome-email link. */
  verifyEmail: (token: string) => http.get<null>('/auth/email/verify', { params: { token }, skipAuth: true }),
}
